// The hole diagram on the scorecard.
//
// Flat illustrated vectors, not satellite imagery. Imagery is what the course was
// traced *from*; it is not what anyone wants to read one-handed, on a phone, in full
// sun, with a group waiting. This costs nothing to draw and nothing to fetch — the
// geometry is already in localStorage by the time a round starts.
//
// The look is a yardage-book illustration: rough underneath, fairway cut into it, then
// water, tee boxes, the green, and bunkers on top. Solid fills with darker outlines
// rather than translucent washes, because washes over a card background turn to mud and
// lose the one thing this drawing is for — telling ground types apart at a glance.
import { html } from "htm/preact";
import { useCallback, useRef, useState } from "preact/hooks";
import type {
  CoursePath,
  CourseShape,
  HoleGeo,
  Landmark,
  LatLng,
  ShapeKind,
} from "../../../shared/types.js";
import {
  holePlan,
  isOnPlan,
  smoothPath,
  teeGlyphPath,
  type PlanPath,
} from "../../../shared/holePlan.js";
import { OSM_ATTRIBUTION } from "../../../shared/overpass.js";

/**
 * Painting order, bottom to top.
 *
 * Fairway first so it reads as cut into the rough. Bunkers last because greenside sand
 * routinely laps over the apron, and a bunker hidden under the green is the one bit of
 * this picture that would change how you play the shot.
 */
const LAYER_ORDER: ShapeKind[] = ["fairway", "apron", "water", "tee", "green", "bunker", "path"];

/**
 * Cart paths bundled into one subpath string per distinct width.
 *
 * Grouping is what makes a network of paths read as one continuous thing: a single
 * stroked element never doubles its own opacity where it crosses or rejoins itself.
 */
function cartPathGroups(paths: PlanPath[]): { widthUnits: number; d: string }[] {
  const byWidth = new Map<number, string[]>();
  for (const p of paths) {
    const key = Math.round(p.widthUnits * 100) / 100;
    const list = byWidth.get(key) ?? [];
    list.push(smoothPath(p.points, false));
    byWidth.set(key, list);
  }
  return [...byWidth.entries()].map(([widthUnits, ds]) => ({ widthUnits, d: ds.join(" ") }));
}

export function HolePlan({
  geo,
  at,
  accuracyYards,
  paths,
  landmarks,
  others,
  hazards,
}: {
  geo: HoleGeo;
  /** Live position, when the player has distances switched on. */
  at?: LatLng | null;
  accuracyYards?: number | null;
  /** Course-level cart paths; the viewport clips whatever runs off the hole. */
  paths?: CoursePath[];
  /** Course-level landmarks, likewise. */
  landmarks?: Landmark[];
  /** The course's other holes, drawn faintly for context where they reach the frame. */
  others?: HoleGeo[];
  /** Course-level bunkers and water, split into live and scenery by the plan. */
  hazards?: CourseShape[];
}) {
  // The shape of the box we're drawn into, measured rather than assumed.
  //
  // The frame is built to match it, so the drawing fills the card instead of being
  // letterboxed into a strip down the middle with dead rough either side. That strip is
  // also where the clip used to cut a cart path or a landmark label in half at an edge
  // with nothing to mark it, which is the visible half of the same bug.
  //
  // A callback ref rather than an effect: the element only exists once there's something
  // to draw, and this way the observer attaches exactly when it does. The element's own
  // size doesn't depend on the viewBox — the stylesheet fixes its height — so measuring
  // it and then reshaping the frame can't feed back into another measurement; the
  // epsilon below is belt and braces for a caller that styles it otherwise.
  const observer = useRef<ResizeObserver | null>(null);
  const [aspect, setAspect] = useState<number | null>(null);
  const measureRef = useCallback((el: SVGSVGElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => {
      const box = el.getBoundingClientRect();
      if (box.width <= 0 || box.height <= 0) return;
      const next = box.width / box.height;
      setAspect((prev) => (prev !== null && Math.abs(prev - next) < 0.01 ? prev : next));
    };
    measure();
    observer.current = new ResizeObserver(measure);
    observer.current.observe(el);
  }, []);

  // Framed on the live position when there is one, so the picture is the shot in front
  // of you rather than a whole hole in which that shot is a few pixels.
  const plan = holePlan(geo, { from: at ?? null, paths, landmarks, others, hazards, aspect });
  // Absent means gone: an untraced hole renders nothing at all, not an empty frame with
  // an apology in it.
  if (!plan) return null;

  // A hazard inside the corridor is part of the shot, so it is painted in the ordinary
  // layer order alongside the hole's own ground rather than as a separate pass — that is
  // what keeps greenside sand lapping over the apron instead of sitting under it.
  const shapes = [...plan.shapes, ...plan.hazards.near].sort(
    (a, b) => LAYER_ORDER.indexOf(a.kind) - LAYER_ORDER.indexOf(b.kind),
  );
  const farHazards = [...plan.hazards.far].sort(
    (a, b) => LAYER_ORDER.indexOf(a.kind) - LAYER_ORDER.indexOf(b.kind),
  );
  const neighbours = plan.neighbours.map((n) => ({
    ...n,
    shapes: [...n.shapes].sort((a, b) => LAYER_ORDER.indexOf(a.kind) - LAYER_ORDER.indexOf(b.kind)),
  }));

  const me = at ? plan.project(at) : null;
  const meVisible = me && isOnPlan(plan, me);
  // Drawn to the same scale as everything else, so a weak fix visibly covers half the
  // fairway rather than quietly claiming a metre.
  const accuracyUnits =
    accuracyYards && plan.yardsPerUnit > 0 ? accuracyYards / plan.yardsPerUnit : 0;

  const disc = plan.greenDisc;
  const pin = plan.greenCenter;

  // Unique per hole so two plans on one page can't share a clip — the scorecard shows
  // one at a time, but the builder's preview is a second copy of this component.
  const clipId = `hp-clip-${geo.hole}`;

  return html`
    <svg
      class="hole-plan"
      ref=${measureRef}
      viewBox=${`0 0 ${plan.width} ${plan.height}`}
      role="img"
      aria-label=${`Plan of hole ${geo.hole}`}
    >
      <!-- Everything the hole is made of, clipped to the frame.

           The clip is what stops a cart path a hundred yards off the hole painting
           across the card. The viewBox is only as wide as the hole and the element is
           as wide as the card, so preserveAspectRatio letterboxes the drawing and
           leaves bars either side - bars that are inside the SVG viewport and therefore
           NOT clipped by it. Anything projected into them, and a course-level cart path
           routinely is, drew on bare card outside the picture.

           The frame is now built to the measured shape of the element, so in a browser
           there are no bars to speak of. The clip stays because that measurement is the
           one thing here that can be missing — first paint, no ResizeObserver, a
           zero-height box mid-transition — and without it the fallback is a cart path
           drawn across bare card.

           The rough those bars want is the element's own background (see the
           stylesheet), which fills the box however the drawing sits inside it. That is
           what the oversized rect here used to be for, and it could never work: it was
           measured in viewBox units, so three times the width of a narrow hole is still
           narrower than the card. -->
      <clipPath id=${clipId}>
        <rect x="0" y="0" width=${plan.width} height=${plan.height} />
      </clipPath>

      <g clip-path=${`url(#${clipId})`}>
      ${/* The holes next door, underneath everything and faded well back.

           This is the layer that answers "am I looking at the right picture?". One
           fairway drawn alone is every fairway on the course; the same fairway with the
           7th running back the other way beside it and a green tucked in behind is a
           place, and it's the one you can see from where you're standing.

           Held down hard on purpose. Full strength it would compete with the hole in
           hand, which is the one thing on this page that has to be unmistakable — hence
           no flag, no tee markers, and a hole number small enough to be read only when
           looked for. */
      neighbours.map(
        (n) => html`
          <g key=${`nb-${n.hole}`} class="hp-other">
            ${/* The fade is on this wrapper rather than on each shape, so a green
                 sitting on its own apron doesn't come out darker than the fairway
                 beside it — per-element opacity compounds where shapes overlap and a
                 group's does not. The number is outside it for the same reason in
                 reverse: it needs to stay readable while the ground goes quiet. */
            html`<g class="hp-other-ground">
              ${n.shapes.map(
                (s, i) => html`
                  <path key=${i} class=${`hp-${s.kind}`} d=${smoothPath(s.points, s.closed)} />
                `,
              )}
            </g>`}
            ${n.label &&
            html`<text class="hp-other-num" x=${n.label.x} y=${n.label.y}>${n.hole}</text>`}
          </g>
        `,
      )}

      ${/* Hazards that reach into the frame but sit outside the playing corridor.

           Above the neighbouring holes and below this hole's own ground, which is where
           they belong: a lake two fairways over is more than scenery and less than
           something you have to carry. The fade is on the wrapper for the same reason
           `hp-other-ground` does it — per-element opacity compounds where shapes
           overlap and a group's does not, and hazards overlap each other constantly. */
      farHazards.length > 0 &&
      html`<g class="hp-far">
        ${farHazards.map(
          (s, i) => html`
            <path key=${`f${i}`} class=${`hp-${s.kind}`} d=${smoothPath(s.points, s.closed)} />
          `,
        )}
      </g>`}

      ${shapes.map(
        (s, i) => html`
          <path key=${i} class=${`hp-${s.kind}`} d=${smoothPath(s.points, s.closed)} />
        `,
      )}

      ${/* What makes a tee box a tee box. Two shades of mown grass can't carry that on
           their own at this size, and a labelled box would be worse — the label would
           be a tee *set* name, which is exactly what this map refuses to store. */
      plan.teeMarkers.map(
        (m, i) => html`<path key=${`tm-${i}`} class="hp-tee-marker" d=${teeGlyphPath(m)} />`,
      )}

      ${/* Cart paths, drawn to their real width so they read as something you could
           put a buggy on rather than as a hairline annotation. Under the green and
           bunkers, since a path runs behind them.

           Everything of the same width goes into ONE path element with several
           subpaths. The stroke is semi-transparent, and separate elements would each
           paint their own — so wherever two stretches met or ran alongside each other
           you'd get a bright seam exactly at the join. One element paints its whole
           stroke once, however much it overlaps itself. */
      cartPathGroups(plan.paths).map(
        (g) => html`
          <path key=${`cp-${g.widthUnits}`} class="hp-cartpath" stroke-width=${g.widthUnits} d=${g.d} />
        `,
      )}

      ${disc &&
      html`<ellipse class="hp-green" cx=${disc.cx} cy=${disc.cy} rx=${disc.rx} ry=${disc.ry} />`}

      ${/* The flag, at the center of the green — the point every yardage here is
           measured to. Drawn last of the course furniture so nothing sits over it. */
      pin &&
      html`
        <g class="hp-pin">
          <line x1=${pin.x} y1=${pin.y} x2=${pin.x} y2=${pin.y - 13} />
          <path d=${`M${pin.x},${pin.y - 13} L${pin.x + 8},${pin.y - 10.5} L${pin.x},${pin.y - 8} Z`} />
          <circle cx=${pin.x} cy=${pin.y} r="1.8" />
        </g>
      `}

      ${/* Landmarks near this hole. Only the ones inside the frame are worth drawing —
           a clubhouse three holes away is noise, and the plan is already tight to the
           ground in front of you. */
      plan.landmarks
        .filter((l) => isOnPlan(plan, l.at))
        .map(
          (l, i) => html`
            <g key=${`lm-${i}`} class=${`hp-landmark hp-lm-${l.kind}`}>
              <circle cx=${l.at.x} cy=${l.at.y} r="4" />
              <text x=${l.at.x} y=${l.at.y - 7}>${l.label}</text>
            </g>
          `,
        )}

      ${/* ODbL asks for this wherever the data is shown, and a hole may carry outlines
           that were imported rather than traced. Cheap, quiet, and in the corner. */
      html`<text class="hp-credit" x=${plan.width - 3} y=${plan.height - 3}>
        ${OSM_ATTRIBUTION}
      </text>`}

      ${meVisible &&
      html`
        <g class="hp-me">
          ${accuracyUnits > 1 &&
          html`<circle class="hp-accuracy" cx=${me!.x} cy=${me!.y} r=${accuracyUnits} />`}
          <circle class="hp-me-dot" cx=${me!.x} cy=${me!.y} r="4" />
        </g>
      `}
      </g>
    </svg>
  `;
}
